import { Controller } from "@hotwired/stimulus"

// Character Sheet Controller
// Handles real-time updates to character gameplay values:
// - Condition tracks (physical/stun damage)
// - Kismet spending/awarding
// - Zuzus management
// - Notes editing
export default class extends Controller {
  static targets = [
    "physicalDamage", "stunDamage",
    "physicalBox", "stunBox",
    "deathSpiralPenalty",
    "kismet", "kismetPool", "totalKismetEarned",
    "zuzus",
    "initiativeValue",
    "ghostRating",
    "notes",
    "charismaBonusPool",
    "saveIndicator"
  ]

  static values = {
    characterId: Number,
    physicalTrackMax: Number,
    stunTrackMax: Number,
    physicalDamage: Number,
    stunDamage: Number,
    kismet: Number,
    kismetPool: Number,
    zuzus: Number,
    initiativeValue: Number
  }

  connect() {
    this.latestRequestId = 0
    this.updateConditionTrackDisplay()
    this.updateDeathSpiralDisplay()
  }

  disconnect() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
  }

  // Condition Track Methods
  clickPhysicalBox(event) {
    const boxNumber = parseInt(event.currentTarget.dataset.box)

    if (this.physicalDamageValue >= boxNumber) {
      // Clicking a filled box - unfill from this box onward
      this.physicalDamageValue = boxNumber - 1
    } else {
      // Clicking an empty box - fill up to this box
      this.physicalDamageValue = boxNumber
    }

    this.updateConditionTrackDisplay()
    this.updateDeathSpiralDisplay()
    this.saveConditionTracks()
  }

  clickStunBox(event) {
    const boxNumber = parseInt(event.currentTarget.dataset.box)

    if (this.stunDamageValue >= boxNumber) {
      this.stunDamageValue = boxNumber - 1
    } else {
      this.stunDamageValue = boxNumber
    }

    this.updateConditionTrackDisplay()
    this.updateDeathSpiralDisplay()
    this.saveConditionTracks()
  }

  updateConditionTrackDisplay() {
    // Update physical track boxes
    this.physicalBoxTargets.forEach(box => {
      const boxNum = parseInt(box.dataset.box)
      if (boxNum <= this.physicalDamageValue) {
        box.classList.add('filled', 'bg-red-500', 'text-white', 'border-red-600')
        box.classList.remove('bg-white', 'dark:bg-gray-700', 'text-gray-600', 'dark:text-gray-300', 'border-gray-400', 'dark:border-gray-500')
      } else {
        box.classList.remove('filled', 'bg-red-500', 'text-white', 'border-red-600')
        box.classList.add('bg-white', 'dark:bg-gray-700', 'text-gray-600', 'dark:text-gray-300', 'border-gray-400', 'dark:border-gray-500')
      }
    })

    // Update stun track boxes
    this.stunBoxTargets.forEach(box => {
      const boxNum = parseInt(box.dataset.box)
      if (boxNum <= this.stunDamageValue) {
        box.classList.add('filled', 'bg-blue-500', 'text-white', 'border-blue-600')
        box.classList.remove('bg-blue-50', 'dark:bg-blue-900/20', 'text-blue-600', 'dark:text-blue-300', 'border-blue-400', 'dark:border-blue-500')
      } else {
        box.classList.remove('filled', 'bg-blue-500', 'text-white', 'border-blue-600')
        box.classList.add('bg-blue-50', 'dark:bg-blue-900/20', 'text-blue-600', 'dark:text-blue-300', 'border-blue-400', 'dark:border-blue-500')
      }
    })

    // Update damage counters if they exist
    if (this.hasPhysicalDamageTarget) {
      this.physicalDamageTarget.textContent = this.physicalDamageValue
    }
    if (this.hasStunDamageTarget) {
      this.stunDamageTarget.textContent = this.stunDamageValue
    }
  }

  updateDeathSpiralDisplay() {
    if (!this.hasDeathSpiralPenaltyTarget) return

    const physicalPenalty = Math.floor(this.physicalDamageValue / 3)
    const stunPenalty = Math.floor(this.stunDamageValue / 3)
    const totalPenalty = physicalPenalty + stunPenalty

    if (totalPenalty > 0) {
      this.deathSpiralPenaltyTarget.textContent = `-${totalPenalty}`
      this.deathSpiralPenaltyTarget.classList.remove('text-green-600', 'dark:text-green-400')
      this.deathSpiralPenaltyTarget.classList.add('text-red-600', 'dark:text-red-400')
    } else {
      this.deathSpiralPenaltyTarget.textContent = '0'
      this.deathSpiralPenaltyTarget.classList.remove('text-red-600', 'dark:text-red-400')
      this.deathSpiralPenaltyTarget.classList.add('text-green-600', 'dark:text-green-400')
    }
  }

  // Kismet Methods
  awardKismet(event) {
    event.preventDefault()
    const amount = this.promptInteger("Enter Kismet amount to award:", "1", { min: 1 })
    if (amount === null) return

    this.saveField('award_kismet', amount)
  }

  spendKismet(event) {
    event.preventDefault()
    const amount = this.promptInteger("Enter Kismet amount to spend:", "1", { min: 1 })
    if (amount === null) return
    if (amount > this.kismetValue) {
      alert(`Not enough Kismet! Have ${this.kismetValue}, trying to spend ${amount}`)
      return
    }

    this.saveField('spend_kismet', amount)
  }

  // Zuzus Methods
  adjustZuzus(event) {
    event.preventDefault()
    const amount = this.promptInteger("Enter amount (negative to subtract):", "0")
    if (amount === null) return

    const newValue = this.zuzusValue + amount
    if (newValue < 0) {
      alert("Cannot have negative Zuzus!")
      return
    }

    this.saveField('zuzus', newValue)
  }

  // Notes Methods
  saveNotes(event) {
    const notes = event.target.value
    this.debouncedSave('notes', notes)
  }

  // Ghost Rating
  setGhostRating(event) {
    const value = event.target.value
    // Allow clearing the ghost rating or setting valid values 2-12
    if (value === '' || value === null) {
      this.saveField('ghost_rating', null)
      return
    }
    const numValue = parseInt(value)
    if (isNaN(numValue) || numValue < 2 || numValue > 12) return
    this.saveField('ghost_rating', numValue)
  }

  // Charisma Bonus Pool
  setCharismaBonusPool(event) {
    const pool = event.target.value
    this.saveField('charisma_bonus_pool', pool)
  }

  submitAdvanceSkill(event) {
    event.preventDefault()

    const { skillKey } = event.currentTarget.dataset
    if (!skillKey) return

    this.saveField('advance_skill', { skill_key: skillKey })
  }

  submitAdvanceAttribute(event) {
    event.preventDefault()

    const { attributeName } = event.currentTarget.dataset
    if (!attributeName) return

    this.saveField('advance_attribute', { attribute_name: attributeName })
  }

  submitPurchaseNewSkill(event) {
    event.preventDefault()

    const button = event.currentTarget
    const pool = this.inlineActionValue(button, 'pool')
    const skillName = this.inlineActionValue(button, 'skillName')

    if (!pool || !skillName) {
      this.showError('Choose a pool and enter a skill name')
      return
    }

    this.saveField('purchase_new_skill', { pool, skill_name: skillName })
  }

  submitSelectKismetBoon(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { boonId, requiresSkill } = button.dataset
    if (!boonId) return

    const skillKey = this.inlineActionValue(button, 'skillKey')
    if (requiresSkill === 'true' && !skillKey) {
      this.showError('Choose a skill for this boon')
      return
    }

    this.saveField('select_kismet_boon', { boon_id: boonId, skill_key: skillKey })
  }

  submitSelectMajorKismetBoon(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { boonId, requiresSkill, requiresPool } = button.dataset
    if (!boonId) return

    const skillKey = this.inlineActionValue(button, 'skillKey')
    const pool = this.inlineActionValue(button, 'pool')

    if (requiresSkill === 'true' && !skillKey) {
      this.showError('Choose a skill for this major boon')
      return
    }

    if (requiresPool === 'true' && !pool) {
      this.showError('Choose a pool for this major boon')
      return
    }

    this.saveField('select_major_kismet_boon', { boon_id: boonId, skill_key: skillKey, pool })
  }

  adjustInitiative(event) {
    event.preventDefault()

    const delta = parseInt(event.currentTarget.dataset.delta, 10)
    if (Number.isNaN(delta)) return

    this.saveField('set_initiative', this.initiativeValueValue + delta)
  }

  submitSetInitiative(event) {
    event.preventDefault()

    const button = event.currentTarget
    const initiative = this.inlineActionInteger(button, 'initiativeValue')
    if (initiative === null) {
      this.showError('Enter a valid initiative value')
      return
    }

    this.saveField('set_initiative', initiative)
  }

  submitAddActiveEffect(event) {
    event.preventDefault()

    const button = event.currentTarget
    const name = this.inlineActionValue(button, 'effectName')
    const source = this.inlineActionValue(button, 'effectSource')
    const duration = this.inlineActionValue(button, 'effectDuration')
    const notes = this.inlineActionValue(button, 'effectNotes')

    if (!name) {
      this.showError('Effect name is required')
      return
    }

    this.saveField('add_active_effect', { name, source, duration, notes })
  }

  submitRuntimeModifier(event) {
    event.preventDefault()

    const button = event.currentTarget
    const modifierId = this.inlineActionValue(button, 'modifierId')
    const payload = this.runtimeModifierPayload(button)
    if (!payload) return

    this.saveField(modifierId ? 'update_runtime_modifier' : 'add_runtime_modifier', {
      modifier_id: modifierId || undefined,
      ...payload
    })
  }

  applyModifierPreset(event) {
    const option = event.currentTarget.selectedOptions[0]
    if (!option) return

    const panel = event.currentTarget.closest('[data-inline-action-panel]')
    if (!panel) return

    const attributeModifiers = JSON.parse(option.dataset.attributeModifiers || '{}')
    const poolModifiers = JSON.parse(option.dataset.poolModifiers || '{}')

    this.setInlinePanelField(panel, 'modifierName', option.dataset.name || '')
    this.setInlinePanelField(panel, 'modifierSource', option.dataset.source || '')
    this.setInlinePanelField(panel, 'modifierDuration', option.dataset.duration || '')
    this.setInlinePanelField(panel, 'modifierNotes', option.dataset.notes || '')
    this.setInlinePanelField(panel, 'modifierInitiative', option.dataset.initiativeModifier || '0')

    ;['strength', 'body', 'reaction', 'intelligence', 'willpower', 'charisma'].forEach(attribute => {
      this.setInlinePanelField(panel, `attribute-${attribute}`, attributeModifiers[attribute] ?? 0)
    })

    ;['brawn', 'finesse', 'focus', 'resolve'].forEach(pool => {
      this.setInlinePanelField(panel, `pool-${pool}`, poolModifiers[pool] ?? 0)
    })
  }

  openNewRuntimeModifierPanel(event) {
    event.preventDefault()

    const panel = document.getElementById('runtime-modifier-panel')
    if (!panel) return

    this.resetRuntimeModifierPanel(panel)
    panel.classList.remove('hidden')
  }

  editRuntimeModifier(event) {
    event.preventDefault()

    const panel = document.getElementById('runtime-modifier-panel')
    if (!panel) return

    const button = event.currentTarget
    const attributeModifiers = JSON.parse(button.dataset.attributeModifiers || '{}')
    const poolModifiers = JSON.parse(button.dataset.poolModifiers || '{}')
    const derivedModifiers = JSON.parse(button.dataset.derivedModifiers || '{}')

    this.resetRuntimeModifierPanel(panel)
    this.setInlinePanelField(panel, 'modifierId', button.dataset.modifierId || '')
    this.setInlinePanelField(panel, 'modifierName', button.dataset.name || '')
    this.setInlinePanelField(panel, 'modifierSource', button.dataset.source || '')
    this.setInlinePanelField(panel, 'modifierDuration', button.dataset.duration || '')
    this.setInlinePanelField(panel, 'modifierNotes', button.dataset.notes || '')
    this.setInlinePanelField(panel, 'modifierInitiative', derivedModifiers.initiative ?? 0)

    ;['strength', 'body', 'reaction', 'intelligence', 'willpower', 'charisma'].forEach(attribute => {
      this.setInlinePanelField(panel, `attribute-${attribute}`, attributeModifiers[attribute] ?? 0)
    })

    ;['brawn', 'finesse', 'focus', 'resolve'].forEach(pool => {
      this.setInlinePanelField(panel, `pool-${pool}`, poolModifiers[pool] ?? 0)
    })

    this.setRuntimeModifierPanelMode(panel, 'edit')
    panel.classList.remove('hidden')
  }

  toggleRuntimeModifier(event) {
    event.preventDefault()

    const { modifierId } = event.currentTarget.dataset
    if (!modifierId) return

    this.saveField('toggle_runtime_modifier', modifierId)
  }

  removeRuntimeModifier(event) {
    event.preventDefault()

    const { modifierId } = event.currentTarget.dataset
    if (!modifierId) return

    this.saveField('remove_runtime_modifier', modifierId)
  }

  setActiveCyberdeck(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('set_active_cyberdeck', runtimeKey)
  }

  toggleInstalledDeckMod(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('toggle_installed_deck_mod', runtimeKey)
  }

  toggleLoadedProgram(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('toggle_loaded_program', runtimeKey)
  }

  toggleActiveIoProgram(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('toggle_active_io_program', runtimeKey)
  }

  submitDeckProgramRating(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { runtimeKey } = button.dataset
    const rating = this.inlineActionInteger(button, 'programRating', { min: 1, max: 6 })
    if (!runtimeKey) return
    if (rating === null) {
      this.showError('Program rating must be between 1 and 6')
      return
    }

    this.saveField('set_deck_program_rating', { runtime_key: runtimeKey, rating })
  }

  submitHackNetworkAccessNode(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { mode } = button.dataset
    const successes = this.inlineActionInteger(button, 'hackSuccesses', { min: 0 })
    if (!mode) return
    if (successes === null) {
      this.showError('Enter a valid success count')
      return
    }

    this.saveField('hack_network_access_node', { mode, successes })
  }

  adjustMatrixAlert(event) {
    event.preventDefault()

    const { delta } = event.currentTarget.dataset
    const parsedDelta = parseInt(delta, 10)
    if (Number.isNaN(parsedDelta)) return

    this.saveField('adjust_matrix_alert', parsedDelta)
  }

  clearMatrixInfluence(event) {
    event.preventDefault()
    this.saveField('clear_matrix_influence', true)
  }

  submitMatrixContext(event) {
    event.preventDefault()

    const button = event.currentTarget
    const name = this.inlineActionValue(button, 'networkAccessNodeName')
    const siteType = this.inlineActionValue(button, 'matrixSiteType')

    if (!name || !siteType) {
      this.showError('Enter a network access node name and site type')
      return
    }

    this.saveField('set_matrix_context', { name, site_type: siteType })
  }

  clearMatrixContext(event) {
    event.preventDefault()
    this.saveField('clear_matrix_context', true)
  }

  submitReduceMatrixAlertWithShadowProtocols(event) {
    event.preventDefault()

    const button = event.currentTarget
    const successes = this.inlineActionInteger(button, 'shadowProtocolSuccesses', { min: 0 })
    if (successes === null) {
      this.showError('Enter a valid success count')
      return
    }

    this.saveField('reduce_matrix_alert_with_shadow_protocols', { successes })
  }

  submitControlMatrixDevice(event) {
    event.preventDefault()

    const button = event.currentTarget
    const name = this.inlineActionValue(button, 'controlledDeviceName')
    const hardening = this.inlineActionInteger(button, 'controlledDeviceHardening', { min: 1 })
    const successes = this.inlineActionInteger(button, 'controlledDeviceSuccesses', { min: 0 })
    const notes = this.inlineActionValue(button, 'controlledDeviceNotes')

    if (!name || hardening === null || successes === null) {
      this.showError('Enter a device name, hardening, and success count')
      return
    }

    this.saveField('control_matrix_device', { name, hardening, successes, notes })
  }

  releaseControlledDevice(event) {
    event.preventDefault()

    const { deviceId } = event.currentTarget.dataset
    if (!deviceId) return

    this.saveField('release_controlled_device', deviceId)
  }

  setActiveVcr(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('set_active_vcr', runtimeKey)
  }

  toggleInstalledRiggingMod(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('toggle_installed_rigging_mod', runtimeKey)
  }

  toggleLinkedRiggingTarget(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('toggle_linked_rigging_target', runtimeKey)
  }

  setHotseatRig(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('set_hotseat_rig', runtimeKey)
  }

  clearHotseatRig(event) {
    event.preventDefault()
    this.saveField('clear_hotseat_rig', true)
  }

  spendRiggingExploitAction(event) {
    event.preventDefault()
    this.saveField('spend_rigging_exploit_action', true)
  }

  refreshRiggingExploitActions(event) {
    event.preventDefault()
    this.saveField('refresh_rigging_exploit_actions', true)
  }

  adjustRiggingTargetPhysicalDamage(event) {
    event.preventDefault()

    const { runtimeKey, delta } = event.currentTarget.dataset
    const parsedDelta = parseInt(delta, 10)
    if (!runtimeKey || Number.isNaN(parsedDelta)) return

    this.saveField('adjust_rigging_target_physical_damage', { runtime_key: runtimeKey, delta: parsedDelta })
  }

  submitRiggingTargetOverdrive(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { runtimeKey } = button.dataset
    const successes = this.inlineActionInteger(button, 'riggingOverdriveSuccesses', { min: 0 })
    if (!runtimeKey) return
    if (successes === null) {
      this.showError('Enter a valid overdrive success count')
      return
    }

    this.saveField('set_rigging_target_overdrive', { runtime_key: runtimeKey, successes })
  }

  spendRiggingTargetMovementAction(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('spend_rigging_target_movement_action', runtimeKey)
  }

  adjustRiggingTargetInertia(event) {
    event.preventDefault()

    const { runtimeKey, delta } = event.currentTarget.dataset
    const parsedDelta = parseInt(delta, 10)
    if (!runtimeKey || Number.isNaN(parsedDelta)) return

    this.saveField('adjust_rigging_target_inertia', { runtime_key: runtimeKey, delta: parsedDelta })
  }

  clearRiggingTargetRoundState(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('clear_rigging_target_round_state', runtimeKey)
  }

  closeRuntimeModifierPanel(event) {
    event.preventDefault()

    const panel = event.currentTarget.closest('[data-inline-action-panel]')
    if (!panel) return

    this.resetRuntimeModifierPanel(panel)
    panel.classList.add('hidden')
  }

  submitRemoveActiveEffect(event) {
    event.preventDefault()

    const { effectId } = event.currentTarget.dataset
    if (!effectId) return

    this.saveField('remove_active_effect', effectId)
  }

  toggleItemEquipped(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('toggle_item_equipped', runtimeKey)
  }

  submitSetWeaponLoadedRounds(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { runtimeKey } = button.dataset
    const loadedRounds = this.inlineActionInteger(button, 'loadedRounds', { min: 0 })
    if (!runtimeKey) return
    if (loadedRounds === null) {
      this.showError('Enter a valid loaded-round count')
      return
    }

    this.saveField('set_weapon_loaded_rounds', { runtime_key: runtimeKey, loaded_rounds: loadedRounds })
  }

  adjustWeaponLoadedRounds(event) {
    event.preventDefault()

    const { runtimeKey, delta } = event.currentTarget.dataset
    const parsedDelta = parseInt(delta, 10)
    if (!runtimeKey || Number.isNaN(parsedDelta)) return

    this.saveField('adjust_weapon_loaded_rounds', { runtime_key: runtimeKey, delta: parsedDelta })
  }

  clearWeaponRuntimeState(event) {
    event.preventDefault()

    const { runtimeKey } = event.currentTarget.dataset
    if (!runtimeKey) return

    this.saveField('clear_weapon_runtime_state', runtimeKey)
  }

  toggleInlineAction(event) {
    event.preventDefault()

    const panelId = event.currentTarget.dataset.panelId
    if (!panelId) return

    const panel = document.getElementById(panelId)
    if (!panel) return

    panel.classList.toggle('hidden')
  }

  closeInlineAction(event) {
    event.preventDefault()

    const panel = event.currentTarget.closest('[data-inline-action-panel]')
    if (!panel) return

    panel.classList.add('hidden')
  }

  purchaseSpeakerBondSlot(event) {
    event.preventDefault()
    const { upgradeId, upgradeName, kismetCost } = event.currentTarget.dataset
    if (!this.confirmAction(`Spend ${kismetCost} Kismet for ${upgradeName}?`)) return

    this.saveField('purchase_speaker_bond_slot', upgradeId)
  }

  purchaseSpeakerInfusionUnlock(event) {
    event.preventDefault()
    const { unlockId, unlockName, kismetCost } = event.currentTarget.dataset
    if (!this.confirmAction(`Spend ${kismetCost} Kismet for ${unlockName}?`)) return

    this.saveField('purchase_speaker_infusion_unlock', unlockId)
  }

  purchaseElementalSoul(event) {
    event.preventDefault()
    const { soulId, soulName, kismetCost } = event.currentTarget.dataset
    if (!this.confirmAction(`Spend ${kismetCost} Kismet for ${soulName}?`)) return

    this.saveField('purchase_elemental_soul', soulId)
  }

  submitSpiritBagPurchase(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { spiritId } = button.dataset
    const rank = this.inlineActionInteger(button, 'rank', { min: 1, max: 6 })
    if (rank === null) {
      this.showError('Spirit bag rank must be between 1 and 6')
      return
    }

    this.saveField('purchase_spirit_bag', { spirit_id: spiritId, rank })
  }

  purchaseSpiritBag(event) {
    event.preventDefault()
    const { spiritId, spiritName, currentRank } = event.currentTarget.dataset
    const suggestedRank = Math.max(parseInt(currentRank, 10) || 1, 1)
    const rank = this.promptInteger(`Choose a spirit bag rank for ${spiritName} (1-6):`, String(suggestedRank), { min: 1 })
    if (rank === null) return

    this.saveField('purchase_spirit_bag', { spirit_id: spiritId, rank })
  }

  submitBindSpeakerSpirit(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { spiritId } = button.dataset
    const force = this.inlineActionInteger(button, 'force', { min: 1 })
    const successes = this.inlineActionInteger(button, 'successes', { min: 0 })
    const bagRank = this.inlineActionInteger(button, 'bagRank', { min: 0 })

    if (force === null || successes === null || bagRank === null) {
      this.showError('Enter valid bind values')
      return
    }

    this.saveField('bind_speaker_spirit', { spirit_id: spiritId, force, successes, bag_rank: bagRank })
  }

  bindSpeakerSpirit(event) {
    event.preventDefault()
    const { spiritId, spiritName, highestBagRank } = event.currentTarget.dataset
    const payload = this.promptSpeakerForcePayload({
      spiritName,
      highestBagRank,
      forceMessage: `Bind ${spiritName}: enter spirit force.`,
      successesMessage: `Bind ${spiritName}: enter Conjuring + Summoning Foci successes.`
    })
    if (!payload) return

    this.saveField('bind_speaker_spirit', { spirit_id: spiritId, ...payload })
  }

  submitInfuseSpeakerSpirit(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { spiritId, source } = button.dataset
    const force = this.inlineActionInteger(button, 'force', { min: 1 })
    const successes = this.inlineActionInteger(button, 'successes', { min: 0 })
    const bagRank = this.inlineActionInteger(button, 'bagRank', { min: 0 })

    if (force === null || successes === null || bagRank === null) {
      this.showError('Enter valid infusion values')
      return
    }

    this.saveField('infuse_speaker_spirit', { spirit_id: spiritId, source, force, successes, bag_rank: bagRank })
  }

  infuseSpeakerSpirit(event) {
    event.preventDefault()
    const { spiritId, spiritName, source, highestBagRank } = event.currentTarget.dataset
    const payload = this.promptSpeakerForcePayload({
      spiritName,
      highestBagRank,
      forceMessage: `Infuse ${spiritName} into ${source}: enter spirit force.`,
      successesMessage: `Infuse ${spiritName}: enter Conjuring + Summoning Foci successes.`
    })
    if (!payload) return

    this.saveField('infuse_speaker_spirit', { spirit_id: spiritId, source, ...payload })
  }

  submitInvokeBoundSpirit(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { spiritId } = button.dataset
    const serviceName = this.inlineActionValue(button, 'serviceName')
    const channelingSuccesses = this.inlineActionInteger(button, 'channelingSuccesses', { min: 0 })

    if (!serviceName || channelingSuccesses === null) {
      this.showError('Choose a service and valid channeling successes')
      return
    }

    this.saveField('invoke_bound_spirit', { spirit_id: spiritId, service_name: serviceName, channeling_successes: channelingSuccesses })
  }

  submitReleaseBoundSpirit(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { spiritId } = button.dataset
    if (!spiritId) return

    this.saveField('release_bound_spirit', spiritId)
  }

  invokeBoundSpirit(event) {
    event.preventDefault()
    const { spiritId, spiritName, serviceList } = event.currentTarget.dataset
    const serviceName = this.promptSpeakerService(spiritName, serviceList)
    if (!serviceName) return

    const channelingSuccesses = this.promptInteger(`Invoke ${spiritName}: enter channeling successes.`, '0', { min: 0 })
    if (channelingSuccesses === null) return

    this.saveField('invoke_bound_spirit', { spirit_id: spiritId, service_name: serviceName, channeling_successes: channelingSuccesses })
  }

  releaseBoundSpirit(event) {
    event.preventDefault()
    const { spiritId, spiritName } = event.currentTarget.dataset
    if (!this.confirmAction(`Release bound spirit ${spiritName}?`)) return

    this.saveField('release_bound_spirit', spiritId)
  }

  submitClearSpeakerInfusion(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { source } = button.dataset
    if (!source) return

    this.saveField('clear_speaker_infusion', source)
  }

  clearSpeakerInfusion(event) {
    event.preventDefault()
    const { source, spiritName } = event.currentTarget.dataset
    if (!this.confirmAction(`Clear ${source} infusion${spiritName ? ` for ${spiritName}` : ''}?`)) return

    this.saveField('clear_speaker_infusion', source)
  }

  submitSpendSpeakerExploitAction(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { spiritId } = button.dataset
    if (!spiritId) return

    this.saveField('spend_speaker_exploit_action', spiritId)
  }

  spendSpeakerExploitAction(event) {
    event.preventDefault()
    const { spiritId, spiritName } = event.currentTarget.dataset
    if (!this.confirmAction(`Spend one exploit action for ${spiritName}?`)) return

    this.saveField('spend_speaker_exploit_action', spiritId)
  }

  submitEndSpeakerCombatAssist(event) {
    event.preventDefault()

    const button = event.currentTarget
    const { spiritId } = button.dataset
    if (!spiritId) return

    this.saveField('end_speaker_combat_assist', spiritId)
  }

  endSpeakerCombatAssist(event) {
    event.preventDefault()
    const { spiritId, spiritName } = event.currentTarget.dataset
    if (!this.confirmAction(`End Assist in Combat for ${spiritName}?`)) return

    this.saveField('end_speaker_combat_assist', spiritId)
  }

  // Full Heal
  fullHeal(event) {
    event.preventDefault()
    if (!this.confirmAction("Fully heal all damage?")) return

    this.physicalDamageValue = 0
    this.stunDamageValue = 0
    this.updateConditionTrackDisplay()
    this.updateDeathSpiralDisplay()
    this.saveConditionTracks()
  }

  // Save Methods
  saveConditionTracks() {
    this.saveFields({
      physical_damage: this.physicalDamageValue,
      stun_damage: this.stunDamageValue
    })
  }

  saveField(field, value) {
    this.saveFields({ [field]: value })
  }

  saveFields(fields) {
    this.showSaving()
    const requestId = ++this.latestRequestId
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
    const csrfToken = document.head.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken

    fetch(`/characters/${this.characterIdValue}/update_sheet`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ character: fields })
    })
      .then(response => response.json())
      .then(data => {
        if (requestId !== this.latestRequestId) return

        if (data.success) {
          this.showSaved()
          this.applyCharacterUpdates(data.character || {})
          if (data.pools_changed || data.sheet_changed) window.location.reload()
        } else {
          this.showError(data.error || 'Save failed')
        }
      })
      .catch(error => {
        if (requestId !== this.latestRequestId) return
        console.error('Save error:', error)
        this.showError('Network error')
      })
  }

  debouncedSave(field, value) {
    clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => {
      this.saveField(field, value)
    }, 500)
  }

  showSaving() {
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.textContent = 'Saving...'
      this.saveIndicatorTarget.classList.remove('text-gray-500', 'dark:text-gray-400', 'text-green-600', 'text-red-600')
      this.saveIndicatorTarget.classList.add('text-yellow-600')
    }
  }

  showSaved() {
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.textContent = 'Saved'
      this.saveIndicatorTarget.classList.remove('text-gray-500', 'dark:text-gray-400', 'text-yellow-600', 'text-red-600')
      this.saveIndicatorTarget.classList.add('text-green-600')
      setTimeout(() => {
        this.showReady()
      }, 2000)
    }
  }

  showError(message) {
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.textContent = message
      this.saveIndicatorTarget.classList.remove('text-gray-500', 'dark:text-gray-400', 'text-yellow-600', 'text-green-600')
      this.saveIndicatorTarget.classList.add('text-red-600')
    }
  }

  showReady() {
    if (!this.hasSaveIndicatorTarget) return

    this.saveIndicatorTarget.textContent = 'Gameplay changes autosave'
    this.saveIndicatorTarget.classList.remove('text-yellow-600', 'text-green-600', 'text-red-600')
    this.saveIndicatorTarget.classList.add('text-gray-500', 'dark:text-gray-400')
  }

  inlineActionValue(button, fieldName) {
    const panel = button.closest('[data-inline-action-panel]')
    const field = panel?.querySelector(`[data-inline-field="${fieldName}"]`)
    return field?.value?.trim()
  }

  setInlinePanelField(panel, fieldName, value) {
    const field = panel?.querySelector(`[data-inline-field="${fieldName}"]`)
    if (!field) return

    field.value = value
  }

  runtimeModifierPayload(button) {
    const name = this.inlineActionValue(button, 'modifierName')
    const source = this.inlineActionValue(button, 'modifierSource')
    const duration = this.inlineActionValue(button, 'modifierDuration')
    const notes = this.inlineActionValue(button, 'modifierNotes')
    const initiativeModifier = this.inlineActionInteger(button, 'modifierInitiative')

    if (!name) {
      this.showError('Modifier name is required')
      return null
    }

    const attributeModifiers = {}
    ;['strength', 'body', 'reaction', 'intelligence', 'willpower', 'charisma'].forEach(attribute => {
      const value = this.inlineActionInteger(button, `attribute-${attribute}`)
      if (value !== null && value !== 0) attributeModifiers[attribute] = value
    })

    const poolModifiers = {}
    ;['brawn', 'finesse', 'focus', 'resolve'].forEach(pool => {
      const value = this.inlineActionInteger(button, `pool-${pool}`)
      if (value !== null && value !== 0) poolModifiers[pool] = value
    })

    return {
      name,
      source,
      duration,
      notes,
      attribute_modifiers: attributeModifiers,
      pool_modifiers: poolModifiers,
      initiative_modifier: initiativeModifier || 0
    }
  }

  resetRuntimeModifierPanel(panel) {
    this.setInlinePanelField(panel, 'modifierId', '')
    this.setInlinePanelField(panel, 'modifierPreset', '')
    this.setInlinePanelField(panel, 'modifierName', '')
    this.setInlinePanelField(panel, 'modifierSource', '')
    this.setInlinePanelField(panel, 'modifierDuration', '')
    this.setInlinePanelField(panel, 'modifierNotes', '')
    this.setInlinePanelField(panel, 'modifierInitiative', 0)

    ;['strength', 'body', 'reaction', 'intelligence', 'willpower', 'charisma'].forEach(attribute => {
      this.setInlinePanelField(panel, `attribute-${attribute}`, 0)
    })

    ;['brawn', 'finesse', 'focus', 'resolve'].forEach(pool => {
      this.setInlinePanelField(panel, `pool-${pool}`, 0)
    })

    this.setRuntimeModifierPanelMode(panel, 'add')
  }

  setRuntimeModifierPanelMode(panel, mode) {
    const title = panel.querySelector('[data-runtime-modifier-title]')
    const submitLabel = panel.querySelector('[data-runtime-modifier-submit-label]')

    if (title) {
      title.textContent = mode === 'edit' ? 'Edit Modifier' : 'Add Modifier'
    }

    if (submitLabel) {
      submitLabel.textContent = mode === 'edit' ? 'Update Modifier' : 'Save Modifier'
    }
  }

  inlineActionInteger(button, fieldName, { min = null, max = null } = {}) {
    const rawValue = this.inlineActionValue(button, fieldName)
    if (rawValue === undefined || rawValue === null || rawValue === '') return null

    const parsedValue = parseInt(rawValue, 10)
    if (Number.isNaN(parsedValue)) return null
    if (min !== null && parsedValue < min) return null
    if (max !== null && parsedValue > max) return null

    return parsedValue
  }

  applyCharacterUpdates(character) {
    if (character.physical_damage !== undefined) {
      this.physicalDamageValue = character.physical_damage
    }

    if (character.stun_damage !== undefined) {
      this.stunDamageValue = character.stun_damage
    }

    if (character.kismet !== undefined) {
      this.kismetValue = character.kismet
      if (this.hasKismetTarget) this.kismetTarget.textContent = character.kismet
    }

    if (character.kismet_pool !== undefined && this.hasKismetPoolTarget) {
      this.kismetPoolTarget.textContent = character.kismet_pool
    }

    if (character.total_kismet_earned !== undefined && this.hasTotalKismetEarnedTarget) {
      this.totalKismetEarnedTarget.textContent = character.total_kismet_earned
    }

    if (character.zuzus !== undefined) {
      this.zuzusValue = character.zuzus
      if (this.hasZuzusTarget) this.zuzusTarget.textContent = character.zuzus.toLocaleString()
    }

    if (character.initiative_value !== undefined) {
      this.initiativeValueValue = character.initiative_value
      if (this.hasInitiativeValueTarget) this.initiativeValueTarget.textContent = character.initiative_value
    }

    if (character.ghost_rating !== undefined && this.hasGhostRatingTarget) {
      this.ghostRatingTarget.value = character.ghost_rating ?? ''
    }

    if (character.charisma_bonus_pool !== undefined && this.hasCharismaBonusPoolTarget) {
      this.charismaBonusPoolTarget.value = character.charisma_bonus_pool || ''
    }

    if (character.physical_damage !== undefined || character.stun_damage !== undefined) {
      this.updateConditionTrackDisplay()
      this.updateDeathSpiralDisplay()
    }
  }

  confirmAction(message) {
    return window.confirm(message)
  }

  promptInteger(message, defaultValue, { min = null } = {}) {
    const rawValue = window.prompt(message, defaultValue)
    if (rawValue === null) return null

    const parsedValue = parseInt(rawValue, 10)
    if (Number.isNaN(parsedValue)) return null
    if (min !== null && parsedValue < min) return null

    return parsedValue
  }

  promptSpeakerForcePayload({ spiritName, highestBagRank, forceMessage, successesMessage }) {
    const force = this.promptInteger(forceMessage, '1', { min: 1 })
    if (force === null) return null

    const successes = this.promptInteger(successesMessage, String(force + 1), { min: 0 })
    if (successes === null) return null

    const suggestedBag = parseInt(highestBagRank, 10) || 0
    const bagRank = this.promptInteger(`Optional spirit bag rank for ${spiritName} (0 for none).`, String(suggestedBag), { min: 0 })
    if (bagRank === null) return null

    return { force, successes, bag_rank: bagRank }
  }

  promptSpeakerService(spiritName, serviceList) {
    const services = (serviceList || '').split('|').filter(Boolean)
    if (services.length === 0) return null

    const servicePrompt = services.map((service, index) => `${index + 1}. ${service}`).join('\n')
    const serviceChoice = window.prompt(`Invoke ${spiritName}: choose a service.\n${servicePrompt}`, '1')
    if (serviceChoice === null) return null

    const serviceIndex = parseInt(serviceChoice, 10)
    if (!Number.isNaN(serviceIndex) && serviceIndex >= 1 && serviceIndex <= services.length) {
      return services[serviceIndex - 1]
    }

    const serviceName = serviceChoice.trim()
    return serviceName || null
  }

}
